/**
@format
@flow
*/

import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import AnimationWrapper from '@ziynx/components/animations';
import { Constants } from '@ziynx/components/layout';
import { withAppManager, getMeasures } from '@ziynx/components/utilities';
import type { ManagedProps } from '@ziynx/flow-types/appManager';

type Props = $ReadOnly<{|

  /**
   * To signal when to open the modal
   * from app level
   */
  open?: boolean,
  /**
   * Width of modal
   * Default to 1004px
   */
  width?: string,
  /**
   * Optional height of modal
   * Defaults to 774px;
   */
  height?: string,
  /**
   * Content to be passed in
   */
  children: Node | any,
|}>;

const MODAL_WIDTH = {
  mobileSmall: '288px',
  mobile: '381px',
  tablet: '382px',
  desktopSmall: '688px',
  default: '1000px',
};

const MODAL_HEIGHT = {
  mobileSmall: '510px',
  mobile: '562px',
  tablet: '501px',
  desktopSmall: '664px',
  default: '812px',
};

const ModalBackground = styled.div`
  display: flex;
  justify-content: center;
  position: fixed;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  background-color: rgba(229, 234, 240, 0.85);
  z-index: 990;
`;

const ModalWrapper = styled.div`
  width: ${({ width, viewport }) => width || getMeasures(MODAL_WIDTH, viewport)};
  height: ${({ height, viewport }) => height || getMeasures(MODAL_HEIGHT, viewport)};
  ${({ viewport }) => viewport === 'desktop' && 'max-height: 90vh;'};
  border-radius: ${Constants.borderRadiusLarge}px;
  background-color: white;
`;

const StyledAnimationWrapper = styled(AnimationWrapper)`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Modal = (props: ManagedProps<Props>): React$Node => {
  const {
    children, height, width, open, viewport, ...rest
  } = props;

  if (!open) return null;

  const ModalComponent = (
    <ModalBackground>
      <StyledAnimationWrapper animated autoReset startAnimation={open} animation="fadeInDown">
        <ModalWrapper width={width} height={height} viewport={viewport} {...rest}>
          {children}
        </ModalWrapper>
      </StyledAnimationWrapper>
    </ModalBackground>

  );

  return createPortal(ModalComponent, document.body);
};

Modal.defaultProps = {
  open: false,
  viewport: 'desktop',
};

export default (withAppManager(Modal): React$ComponentType<
  React$Config<ManagedProps<Props>, typeof Modal.defaultProps>>);
